import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, Location } from '@angular/common';
import { EnvironmentService } from './environment.service';
import { GlobalEventsService } from './global-events.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { StaticContentService } from './static-content.service';
import { shareReplay } from 'rxjs';
import { REQUEST } from '../../../express.tokens';
import { filterFirst } from 'ngx-unificator/rxjs';
import { HIDDEN_LANG, ROOT_LANG } from '../helpers/router';
import { LanguageService } from './language.service';
import { isMirror } from '../helpers/mirror';
import { HostRequestsInterceptor } from '../interceptors/host-requests.interceptor';
import { PlatformService } from 'ngx-unificator/services';

@Injectable({
  providedIn: 'root',
})
export class SeoService {

  private _meta = inject(Meta);
  private _title = inject(Title);
  private _platform = inject(PlatformService);
  private _location = inject(Location);
  private _lang = inject(LanguageService);
  private _env = inject(EnvironmentService);
  private _globalEvents = inject(GlobalEventsService);
  private _document = inject<Document>(DOCUMENT);
  private _static = inject(StaticContentService);
  private _injector = inject(Injector);
  private _host = inject(HostRequestsInterceptor);

  private _mapPrefixMirrorsLocales: WritableSignal<Record<string, { hreflang: string, domain?: string }>> = signal({});
  public currentPageUrl = signal<string>(undefined);
  public domain = signal<string>(undefined);

  private _hreflangMap$ = this._static.item({ slug: 'seo-hreflang' }).pipe(
    filterFirst(data => !!data),
    map(item => item[0]?.Grouplist),
    tap((data) => this._mapPrefixMirrorsLocales.set(data)),
    shareReplay(1),
  );

  /**
   * Set in head Title & description
   */
  public setMetaTags(tags: { MetaTitle?: string; MetaDesc?: string; NoIndex?: boolean }) {
    this._title.setTitle(tags?.MetaTitle || '');
    this._meta.updateTag({ name: 'description', content: tags?.MetaDesc || '' });
    if (tags?.NoIndex) {
      this.addMetaRobotNoIndex();
    }
  }

  /**
   * Add new alternate/canonical links and remove old links
   */
  public updateGlobalHeadLink() {
    this._addLinkPreconnect();
    this._resolveIsMirror();
    this._globalEvents.routerNavigationEnd$.pipe(
    ).subscribe((e) => {
      this._addLocaleToNoindex(e.url, HIDDEN_LANG);
      this._removeOldLinks(['alternate', 'canonical', 'x-default']);
      this._updateHeadLinks();
      this._addLangTagForHTML();
      this._setSeoDomainData(e);
    });
  }

  private _buildUrl(locale: string, domain: string, cleanPath: string): string {
    const base = `https://www.winthrone${domain}.com`;
    return locale === ROOT_LANG || locale === ''
      ? cleanPath != '' || locale === ROOT_LANG || locale === '' ?
        `${base}/${cleanPath}` : `${base}`
      : cleanPath != '' || locale === ROOT_LANG || locale === '' ?
        `${base}/${locale}/${cleanPath}` : `${base}/${locale}`;
  }

  private _addAlternateLinks(cleanPath: string, locales: string[]) {

    const hreflangMap = this._mapPrefixMirrorsLocales();

    locales
      .filter(locale => locale !== HIDDEN_LANG)
      .forEach(locale => {
        const { domain = '', hreflang } = hreflangMap[locale] ?? {};
        const link = this._document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('href', this._buildUrl(locale, domain, cleanPath));
        if (hreflang) {
          link.setAttribute('hreflang', hreflang);
        }
        this._document.head.appendChild(link);
      });

    const defaultLink = this._document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('href', cleanPath != '' ? `https://www.winthrone.com/${cleanPath}` : `https://www.winthrone.com/`);
    defaultLink.setAttribute('hreflang', 'x-default');
    this._document.head.appendChild(defaultLink);
  }

  private _addCanonicalLink(cleanPath: string) {
    const hreflangMap = this._mapPrefixMirrorsLocales();
    const currentLocale = this._lang.currentRouteLanguage;
    const { domain = '' } = hreflangMap[currentLocale] ?? {};
    const canonicalLink = this._document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', this._buildUrl(currentLocale, domain, cleanPath));
    this._document.head.appendChild(canonicalLink);
  }

  private _updateHeadLinks() {
    const locales = this._env.dataEnv()?.languageList?.map(item => item.short);
    const segments = this._location.path()?.split(/[?#]/)[0]?.split('/')?.filter(Boolean);
    const cleanPath = locales?.includes(segments[0])
      ? segments.slice(1).join('/')
      : segments.join('/');

    this._addAlternateLinks(cleanPath, locales);
    this._addCanonicalLink(cleanPath);
  }

  /**
   * Remove old alternate\canonical links from DOM
   */
  private _removeOldLinks(typeOfLinks: string[]) {
    typeOfLinks.forEach(rel => {
      this._document.querySelectorAll(`link[rel="${rel}"]`)
        .forEach(link => this._document.head.removeChild(link));
    });
  }

  /**
   * Add in head <meta name="robots" content="noindex" />
   */
  public addMetaRobotNoIndex() {
    this._meta.addTags([{ name: 'robots', content: 'noindex' }]);
  }

  /**
   * Add in head <meta name="robots" content="noindex, nofollow" />
   */
  public addMetaRobotNoIndexNoFollow() {
    this._meta.addTags([{ name: 'robots', content: 'noindex, nofollow' }]);
  }

  /**
   * Sets the `lang` attribute of the HTML <html> element
   */
  private _addLangTagForHTML() {
    const matches = this._location.path().match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
    this._document.documentElement.lang =
      matches && this._env.dataEnv()?.languageList.some(e => e.short === matches[1])
        ? matches[1]
        : ROOT_LANG;
  }

  /**
   * Returns hostname on which app running
   * @private
   */
  private _getAppHost(): string | undefined {
    const host = this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
    return `https://${host}`;
  }

  private _addLinkPreconnect() {
    const link: HTMLLinkElement = this._document.createElement('link');
    const mediaDomain = this._getAppHost()
      .replace('www.', 'media.')
      .replace('stage.', 'stage-media.');
    link.setAttribute('rel', 'preconnect');
    link.setAttribute('href', mediaDomain);
    this._document.head.appendChild(link);
  }

  public addLinkPreloadImg(imgPath: string) {
    if (!this._platform.isBrowser) {
      const link: HTMLLinkElement = this._document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('fetchPriority', 'high');
      link.setAttribute('as', 'image');
      link.setAttribute('href', imgPath);
      this._document.head.appendChild(link);
    }
  }

  private _addLocaleToNoindex(url: string, lang: string) {
    if (url.startsWith(`/${lang}`) && !url.startsWith(`/${lang}-`)) {
      this._meta.addTags([{ name: 'robots', content: 'noindex, nofollow' }]);
    }
  }

  /**
   * The function `_resolveIsMirror` determines if the current domain is a mirror site based on the host
   * information.
   */
  private _resolveIsMirror() {
    const host = this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
    const parts = host?.replace(/^www\./, '').split('.');
    let domain = null;
    if (parts.length > 2 && parts[parts.length - 2].length <= 3) {
      domain = parts[parts.length - 3];
    }
    domain = parts.length > 1 ? parts[parts.length - 2] : parts[0];
    isMirror.set(domain !== 'winthrone');
  }

  private _setSeoDomainData(data) {
    this.domain.set(this._host.getDomainName()?.includes('localhost') ? this._host.getDomainName() : `https://${this._host.getDomainName()}`);
    const splitUrl = data?.url?.split('/');
    this.currentPageUrl.set(!this._lang.currentRouteLanguage ? splitUrl?.[1] : (splitUrl?.[2] || ''));
  }

}
